
import React, { useState } from "react";
import { AlertTriangle, X, Trash2 } from 'lucide-react';

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Delete Item",
  itemName = "",
  itemType = "item",
  isLoading = false,
  warningMessage = null,
  itemDetails = null
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  
  if (!isOpen) return null;

  const requireTextConfirmation = itemType === 'inventory' && itemDetails?.itemCount > 0;
  const expectedText = `delete ${itemName}`;

  const handleConfirm = () => {
    if (requireTextConfirmation && confirmText.toLowerCase() !== expectedText.toLowerCase()) {
      return;
    }
    onConfirm();
  };

  const handleClose = () => {
    setConfirmText('');
    setShowTextInput(false);
    onClose();
  };

  const isConfirmDisabled = requireTextConfirmation && confirmText.toLowerCase() !== expectedText.toLowerCase();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Are you sure you want to delete this {itemType}?
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="font-medium text-gray-900">{itemName}</p>
              {itemDetails && (
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  {itemDetails.description && (
                    <p><span className="font-medium">Description:</span> {itemDetails.description}</p>
                  )}
                  {typeof itemDetails.itemCount !== 'undefined' && (
                    <p><span className="font-medium">Items:</span> {itemDetails.itemCount}</p>
                  )}
                  {typeof itemDetails.totalWeight !== 'undefined' && (
                    <p><span className="font-medium">Total Weight:</span> {itemDetails.totalWeight} kg</p>
                  )}
                  {itemDetails.createdAt && (
                    <p><span className="font-medium">Created:</span> {new Date(itemDetails.createdAt).toLocaleDateString()}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Warning message */}
          {warningMessage && (
            <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <div className="flex">
                <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-yellow-700">{warningMessage}</p>
              </div>
            </div>
          )}

          {/* Special warning for inventories with items */}
          {itemType === 'inventory' && itemDetails?.itemCount > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-red-800 mb-1">
                    This inventory contains {itemDetails.itemCount} item{itemDetails.itemCount !== 1 ? 's' : ''}
                  </h4>
                  <p className="text-sm text-red-700 mb-3">
                    Deleting this inventory will permanently remove all items inside it. This action cannot be undone.
                  </p>
                  
                  {!showTextInput ? (
                    <button
                      onClick={() => setShowTextInput(true)}
                      className="text-sm text-red-600 hover:text-red-800 underline"
                    >
                      I understand, proceed with deletion
                    </button>
                  ) : (
                    <div>
                      <p className="text-sm text-red-700 mb-2">
                        Type <span className="font-mono bg-red-100 px-1 rounded">{expectedText}</span> to confirm:
                      </p>
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        className="w-full px-3 py-2 border border-red-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                        placeholder={expectedText}
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="text-sm text-gray-500">
            <strong>This action cannot be undone.</strong> This will permanently delete the {itemType} and all associated data.
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading || isConfirmDisabled}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={16} />
                Delete {itemType}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal